class ViewModeToggle {
  static MODES = {
    SUMMARY: 'summary',
    DETAIL: 'detail',
  };

  static ANIMATION_DURATION = 300;

  static RESIZE_DEBOUNCE = 250;

  static SCROLL_OFFSET = 100;

  static COLUMN_PADDING = 20;

  constructor(container) {
    this.baseClass = 'brand-venues';
    this.container = container;
    this.elements = this.cacheElements();
    this.currentMode = ViewModeToggle.MODES.SUMMARY;

    this.init();
  }

  cacheElements() {
    return {
      cardsContainer: this.container.querySelector(`.${this.baseClass}__cards`),
      viewModeButtons: this.container.querySelectorAll(`.${this.baseClass}__view-mode`),
      cardInners: this.container.querySelectorAll(`.${this.baseClass}__card-inner`),
      cards: this.container.querySelectorAll(`.${this.baseClass}__card`),
      accordionButtons: this.container.querySelectorAll(`.${this.baseClass}__accordion-button`),
      accordionButtonIcons: this.container.querySelectorAll(`.${this.baseClass}__accordion-button-icon`),
      capacityChartLinks: this.container.querySelectorAll(`.${this.baseClass}__link--capacity-chart`),
    };
  }

  init() {
    if (!this.elements.viewModeButtons.length || !this.elements.cardsContainer) {
      return;
    }

    this.initializeAccordions();

    this.elements.viewModeButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        this.toggleViewMode(e);
      });
    });

    this.bindAccordionEvents();

    this.bindCapacityChartEvents();

    this.setViewMode(this.currentMode);

    this.bindResizeEvent();
  }

  toggleViewMode(e) {
    e.preventDefault();
    const targetMode = e.target.getAttribute('data-mode');

    if (targetMode && targetMode !== this.currentMode) {
      this.setViewMode(targetMode);
    }
  }

  setViewMode(mode) {
    this.currentMode = mode;

    this.elements.cardsContainer.classList.remove(
      `${this.baseClass}__cards--${ViewModeToggle.MODES.SUMMARY}`,
      `${this.baseClass}__cards--${ViewModeToggle.MODES.DETAIL}`,
    );
    this.elements.cardsContainer.classList.add(`${this.baseClass}__cards--${mode}`);

    this.updateViewModeButtons(mode);

    this.updateCardDisplay();
  }

  updateViewModeButtons(activeMode) {
    this.elements.viewModeButtons.forEach((button) => {
      const buttonMode = button.getAttribute('data-mode');
      const isActive = buttonMode === activeMode;

      button.classList.toggle(`${this.baseClass}__view-mode--active`, isActive);
      button.setAttribute('aria-pressed', isActive.toString());
    });
  }

  updateCardDisplay() {
    this.elements.cardInners.forEach((cardInner) => {
      const isSummary = cardInner.classList.contains(`${this.baseClass}__card-inner--summary`);
      const isDetail = cardInner.classList.contains(`${this.baseClass}__card-inner--detail`);

      if (isSummary) {
        // eslint-disable-next-line no-param-reassign
        cardInner.style.display = this.currentMode === ViewModeToggle.MODES.SUMMARY ? 'flex' : 'none';
      } else if (isDetail) {
        // eslint-disable-next-line no-param-reassign
        cardInner.style.display = this.currentMode === ViewModeToggle.MODES.DETAIL ? 'flex' : 'none';
      }
    });
  }

  switchToMode(mode) {
    if (Object.values(ViewModeToggle.MODES).includes(mode)) {
      this.setViewMode(mode);
    }
  }

  getCurrentMode() {
    return this.currentMode;
  }

  initializeAccordions() {
    this.setEqualColumnWidths();

    this.elements.accordionButtons.forEach((button) => {
      const card = button.closest(`.${this.baseClass}__card`);
      const accordion = card.querySelector(`.${this.baseClass}__accordion`);
      const tableWrapper = accordion.querySelector(`.${this.baseClass}__table-wrapper`);
      const image = accordion.querySelector(`.${this.baseClass}__image--detail`);
      const hotelName = card.querySelector(`.${this.baseClass}__name`)?.textContent || 'Hotel';

      const accordionId = `accordion-${hotelName.toLowerCase().replace(/\s+/g, '-')}`;
      const buttonId = `button-${hotelName.toLowerCase().replace(/\s+/g, '-')}`;

      button.setAttribute('aria-controls', accordionId);
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-label', `Toggle details for ${hotelName}`);
      button.setAttribute('id', buttonId);

      accordion.setAttribute('aria-labelledby', buttonId);
      accordion.setAttribute('role', 'region');
      accordion.setAttribute('id', accordionId);

      if (tableWrapper) {
        this.setAccordionState(accordion, button, tableWrapper, image, accordion.classList.contains(`${this.baseClass}__accordion--active`));
      }
    });
  }

  setEqualColumnWidths() {
    const tables = this.container.querySelectorAll(`.${this.baseClass}__table--detail`);

    if (!tables.length) return;

    const firstTable = tables[0];
    let firstRow = firstTable.querySelector('tbody tr');
    if (!firstRow) {
      firstRow = firstTable.querySelector('thead tr');
    }
    if (!firstRow) return;

    const columnCount = firstRow.children.length;

    const tempElement = this.createTextMeasurementElement();

    // Measure every column first, write afterwards: interleaving the two forced a
    // full layout for each column. The measuring element is absolutely positioned
    // and independent of the tables, so the widths are the same.
    const widthCache = new Map();
    const widths = [];

    for (let colIndex = 0; colIndex < columnCount; colIndex++) {
      widths.push(this.calculateMaxColumnWidth(tables, colIndex, tempElement, widthCache));
    }

    widths.forEach((maxWidth, colIndex) => {
      if (maxWidth > 0) {
        this.applyColumnWidth(tables, colIndex, maxWidth);
      }
    });

    document.body.removeChild(tempElement);
  }

  // eslint-disable-next-line class-methods-use-this
  createTextMeasurementElement() {
    const tempElement = document.createElement('div');
    Object.assign(tempElement.style, {
      position: 'absolute',
      visibility: 'hidden',
      height: 'auto',
      width: 'auto',
      whiteSpace: 'nowrap',
      fontSize: '15px',
      fontFamily: 'inherit',
    });
    document.body.appendChild(tempElement);
    return tempElement;
  }

  static measureText(tempElement, text, widthCache) {
    if (!widthCache.has(text)) {
      // eslint-disable-next-line no-param-reassign
      tempElement.textContent = text;
      widthCache.set(text, tempElement.offsetWidth);
    }
    return widthCache.get(text);
  }

  // eslint-disable-next-line class-methods-use-this
  calculateMaxColumnWidth(tables, colIndex, tempElement, widthCache = new Map()) {
    let maxWidth = 0;

    tables.forEach((table) => {
      const tbodyRows = table.querySelectorAll('tbody tr');
      tbodyRows.forEach((row) => {
        const cell = row.children[colIndex];
        if (cell) {
          const text = cell.textContent.trim();
          const textWidth = ViewModeToggle.measureText(tempElement, text, widthCache);
          maxWidth = Math.max(maxWidth, textWidth);
        }
      });

      const theadRows = table.querySelectorAll('thead tr');
      theadRows.forEach((row) => {
        const cell = row.children[colIndex];
        if (cell) {
          const text = cell.textContent.trim();
          const textWidth = ViewModeToggle.measureText(tempElement, text, widthCache);
          maxWidth = Math.max(maxWidth, textWidth);
        }
      });
    });

    return maxWidth + ViewModeToggle.COLUMN_PADDING;
  }

  // eslint-disable-next-line class-methods-use-this
  applyColumnWidth(tables, colIndex, width) {
    const widthPx = `${width}px`;

    tables.forEach((table) => {
      const tbodyRows = table.querySelectorAll('tbody tr');
      tbodyRows.forEach((row) => {
        const cell = row.children[colIndex];
        if (cell) {
          Object.assign(cell.style, {
            width: widthPx,
            minWidth: widthPx,
            maxWidth: widthPx,
          });
        }
      });

      const theadRows = table.querySelectorAll('thead tr');
      theadRows.forEach((row) => {
        const cell = row.children[colIndex];
        if (cell) {
          Object.assign(cell.style, {
            width: widthPx,
            minWidth: widthPx,
            maxWidth: widthPx,
          });
        }
      });
    });
  }

  bindAccordionEvents() {
    this.elements.accordionButtonIcons.forEach((icon) => {
      icon.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const button = icon.closest(`.${this.baseClass}__accordion-button`);
        if (button) {
          this.handleAccordionToggle(button);
        }
      });

      const button = icon.closest(`.${this.baseClass}__accordion-button`);
      if (button) {
        button.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleAccordionToggle(button);
          } else if (e.key === 'Escape') {
            this.handleAccordionEscape(button);
          }
        });
      }
    });
  }

  handleAccordionToggle(button) {
    const accordion = this.getAccordionFromButton(button);
    if (accordion) {
      this.toggleAccordion(accordion);
    }
  }

  handleAccordionEscape(button) {
    const accordion = this.getAccordionFromButton(button);
    if (accordion && button.getAttribute('aria-expanded') === 'true') {
      this.toggleAccordion(accordion);
    }
  }

  getAccordionFromButton(button) {
    const card = button.closest(`.${this.baseClass}__card`);
    return card?.querySelector(`.${this.baseClass}__accordion`);
  }

  bindCapacityChartEvents() {
    this.elements.capacityChartLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const hotelName = link.dataset.hotel;
        this.switchToDetailAndScrollToHotel(hotelName);
      });
    });
  }

  bindResizeEvent() {
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.setEqualColumnWidths();
      }, ViewModeToggle.RESIZE_DEBOUNCE);
    });
  }

  setAccordionState(accordion, button, tableWrapper, image, isActive) {
    const detailLinks = accordion.querySelector(`.${this.baseClass}__links--detail`);
    const tableBody = accordion.querySelector(`.${this.baseClass}__body`);
    const actionCards = accordion.querySelectorAll(`.${this.baseClass}__action-card`);
    const card = accordion.closest(`.${this.baseClass}__card`);

    if (isActive) {
      accordion.classList.add(`${this.baseClass}__accordion--active`);
      button.setAttribute('aria-expanded', 'true');
      accordion.setAttribute('aria-hidden', 'false');

      if (card) {
        card.classList.remove(`${this.baseClass}__card--narrow`);
      }

      if (tableBody) {
        tableBody.style.display = 'table-row-group';
        tableBody.style.opacity = '1';
      }

      if (image) {
        // eslint-disable-next-line no-param-reassign
        image.style.display = 'block';
        // eslint-disable-next-line no-param-reassign
        image.style.opacity = '1';
      }

      if (detailLinks) {
        detailLinks.style.display = 'flex';
        detailLinks.style.opacity = '1';
      }

      // eslint-disable-next-line no-shadow
      actionCards.forEach((card) => {
        card.removeAttribute('tabindex');
        card.removeAttribute('aria-hidden');
      });

      button.classList.add(`${this.baseClass}__accordion-button--expanded`);
    } else {
      accordion.classList.remove(`${this.baseClass}__accordion--active`);
      button.setAttribute('aria-expanded', 'false');
      accordion.setAttribute('aria-hidden', 'true');

      if (tableBody) {
        tableBody.style.opacity = '0';
        setTimeout(() => {
          tableBody.style.display = 'none';
        }, ViewModeToggle.ANIMATION_DURATION);
      }

      if (tableWrapper) {
        // eslint-disable-next-line no-param-reassign
        tableWrapper.scrollLeft = 0;
      }

      if (image) {
        // eslint-disable-next-line no-param-reassign
        image.style.opacity = '0';
      }

      if (detailLinks) {
        detailLinks.style.opacity = '0';
      }

      // eslint-disable-next-line no-shadow
      actionCards.forEach((card) => {
        card.setAttribute('tabindex', '-1');
        card.setAttribute('aria-hidden', 'true');
      });

      setTimeout(() => {
        if (image) {
          // eslint-disable-next-line no-param-reassign
          image.style.display = 'none';
        }
        if (detailLinks) {
          detailLinks.style.display = 'none';
        }
      }, ViewModeToggle.ANIMATION_DURATION);

      button.classList.remove(`${this.baseClass}__accordion-button--expanded`);

      if (card) {
        card.classList.add(`${this.baseClass}__card--narrow`);
      }
    }
  }

  toggleAccordion(accordion) {
    if (!accordion) return;

    const card = accordion.closest(`.${this.baseClass}__card`);
    const button = card.querySelector(`.${this.baseClass}__accordion-button`);
    const tableWrapper = accordion.querySelector(`.${this.baseClass}__table-wrapper`);
    const image = accordion.querySelector(`.${this.baseClass}__image--detail`);

    if (!button || !tableWrapper) return;

    const isActive = accordion.classList.contains(`${this.baseClass}__accordion--active`);
    const newState = !isActive;

    this.setAccordionState(accordion, button, tableWrapper, image, newState);

    if (newState) {
      const detailLinks = accordion.querySelector(`.${this.baseClass}__links--detail`);
      const tableBody = accordion.querySelector(`.${this.baseClass}__body`);

      if (tableBody) {
        tableBody.style.opacity = '0';
      }
      if (image) {
        image.style.opacity = '0';
      }
      if (detailLinks) {
        detailLinks.style.opacity = '0';
      }

      requestAnimationFrame(() => {
        if (tableBody) {
          tableBody.style.opacity = '1';
        }
        if (image) {
          image.style.opacity = '1';
        }
        if (detailLinks) {
          detailLinks.style.opacity = '1';
        }
      });
    }
  }

  switchToDetailAndScrollToHotel(hotelName) {
    const detailMode = this.container.querySelector(`.${this.baseClass}__view-mode[data-mode="${ViewModeToggle.MODES.DETAIL}"]`);
    if (detailMode) {
      this.switchToMode(ViewModeToggle.MODES.DETAIL);
    }

    const targetCard = this.container.querySelector(`.${this.baseClass}__link--capacity-chart[data-hotel="${hotelName}"]`)?.closest(`.${this.baseClass}__card`);
    if (targetCard) {
      const accordion = targetCard.querySelector(`.${this.baseClass}__accordion`);
      if (accordion && !accordion.classList.contains(`${this.baseClass}__accordion--active`)) {
        this.toggleAccordion(accordion);
      }

      setTimeout(() => {
        const cardRect = targetCard.getBoundingClientRect();
        const scrollTop = window.pageYOffset + cardRect.top - ViewModeToggle.SCROLL_OFFSET;

        window.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });
      }, 100);
    }
  }
}

export default ViewModeToggle;
